import { isEmpty } from "ramda";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import apiConfig from "../../config";
import {
  getObjectFromLocalStore,
  setObjectInLocalStore,
  removeLocalStore,
  clearLocalStore,
} from "../utils/localStore";
import { resetLoginState, resetLogoutState, setFCMToken } from "app/redux";
import { clearSessionStore } from "app/utils/sessionStore";
import { store } from "@store";
import { persistStore } from "redux-persist";
import Cookies from "js-cookie";
import { getCookieDomain } from "./utils";
import { setNeioAccessToken } from "lib/utils";

interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  userType: string;
}
const { baseURL, paths, headers, excludeToken } = apiConfig;
const instance: AxiosInstance = axios.create({
  baseURL,
  headers: {
    ...headers,
  },
});
function urlBuild(endpoint: string, key?: string, requestParams?: boolean, queryParams?: boolean) {
  if (paths[endpoint] == null) {
    throw new Error(`Endpoint is not present in ${Object.keys(apiConfig.paths)}`);
  }

  return `${instance.defaults.baseURL}${paths[endpoint]}${
    requestParams ? `/${key}` : queryParams ? `?${key}` : ""
  }`;
}
async function requestRefreshToken() {
  const { refresh_token } = getObjectFromLocalStore("useraccess");
  const header: any = {
    headers: {
      Authorization: `Bearer ${refresh_token}`,
    },
  };
  try {
    // NOTE update the API URL according to need
    const { data } = await instance.post<RefreshTokenResponse>(
      urlBuild("refreshToken"),
      {},
      header,
    );
    return data;
  } catch (error) {
    clearSessionStore();
    return Promise.reject(error);
  }
}

let isRefreshing = false;
// const refreshPromiseQueue: Array<{
//   resolve: (value?: unknown) => void;
//   reject: (reason?: any) => void;
// }> = [];
// NOTE : GET - await MakeRequest("/api/path/12").get({querry1: 1, querry2: 2})
// NOTE : POST - await MakeRequest("/api/path").post({param1: 1, param2: 2})
// NOTE : PUT - await MakeRequest("/api/path/12").get({querry1: 1, querry2: 2})
// NOTE : DELETE - await MakeRequest("/api/path/12").delete({querry1: 1, querry2: 2})
// NOTE : we can even further add file download feature in it
instance.interceptors.request.use(
  async (config) => {
    const path = instance.defaults.baseURL
      ? config?.url?.replace(instance.defaults.baseURL, "").split("?")[0]
      : "";
    const userData = getObjectFromLocalStore("useraccess");
    const assistantAgentUserData = getObjectFromLocalStore("assistantAgentUserAccess");

    // Add authorization header with access token
    if (path && !excludeToken?.includes(path) && !isEmpty(config?.headers) && userData) {
      config.headers.Authorization = `Bearer ${
        userData?.isAssistant &&
        !isEmpty(assistantAgentUserData) &&
        path !== "/profile/agent/" &&
        !path.includes("/profile/pictureUpload") &&
        !path.includes("/profile/profilePicture") &&
        path !== "/lookup/after"
          ? assistantAgentUserData.access_token
          : userData.access_token
      }`;
    }

    if (config.data instanceof FormData && config.headers) {
      const { headers: requestHeaders } = config as { headers: any };
      if (typeof requestHeaders.delete === "function") {
        requestHeaders.delete("Content-Type");
        requestHeaders.delete("content-type");
      }
      delete requestHeaders["Content-Type"];
      delete requestHeaders["content-type"];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  (response: AxiosResponse) =>
    // Do something with successful response
    response,
  async (error: any) => {
    const { response, config: originalRequest } = error;
    const persistor = persistStore(store);
    if (
      response &&
      response.status === 401 &&
      response.data.type === "OAuthException" &&
      originalRequest
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const userAccess: any = await requestRefreshToken();
          if (userAccess) {
            setObjectInLocalStore("useraccess", userAccess);
            setNeioAccessToken();
            // Process all the queued requests
            return await instance(originalRequest);
          }
          removeLocalStore("useraccess");
          store.dispatch(setFCMToken(false));
          isRefreshing = false;
        } catch (errorResponse) {
          // Handle refresh token error
          /* If the refresh token is expired or we are getting an unauthorized
          access error clean the refresh token and reload the window */
          removeLocalStore("useraccess");
          store.dispatch(setFCMToken(false));
          setTimeout(() => window.location.reload(), 300);
          // return Promise.reject(errorResponse);
        }
      }
    }
    if (
      originalRequest?.url?.includes(paths.logout) ||
      (response && response.status === 401 && response.data.type === "OAuthInvalid")
    ) {
      const cookieDomain = getCookieDomain();
      Cookies.remove("authData", {
        path: "/",
        domain: cookieDomain,
      });
      removeLocalStore("useraccess");
      persistor.purge();
      clearSessionStore();
      store.dispatch(resetLogoutState());
      store.dispatch(resetLoginState());
      store.dispatch(setFCMToken(false));
      clearLocalStore();
      window.location.replace("/");
    }
    const errorDetails = { ...response?.data, statusCode: response?.status };
    return Promise.reject(errorDetails);
  },
);
export function makeRequest(
  path: string,
  headerData?: Record<string, any>,
  config?: Record<string, any>,
) {
  const header: any = {
    ...config,
    headers: {
      ...(headerData || {}),
      ...(config?.headers || {}),
    },
  };
  const getRequestHeader = (body?: any) => {
    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
    if (isFormData) {
      const formHeaders = {
        ...(headerData || {}),
        ...(config?.headers || {}),
      };
      delete formHeaders["Content-Type"];
      delete formHeaders["content-type"];
      return {
        ...config,
        headers: formHeaders,
      };
    }
    return header;
  };
  const request = {
    get: async (key?: string, requestParams?: boolean, queryParams?: boolean) => {
      try {
        return await instance.get(urlBuild(path, key, requestParams, queryParams), header);
      } catch (error: any) {
        error.infoStatus = "error";
        throw error;
      }
    },

    post: async (body?: any, key?: string, requestParams?: boolean, queryParams?: boolean) => {
      try {
        return await instance.post(
          urlBuild(path, key, requestParams, queryParams),
          body,
          getRequestHeader(body),
        );
      } catch (error: any) {
        error.infoStatus = "error";
        throw error;
      }
    },

    put: async (key?: string, body: any = {}, requestParams?: boolean, queryParams?: boolean) => {
      try {
        return await instance.put(
          urlBuild(path, key, requestParams, queryParams),
          body,
          getRequestHeader(body),
        );
      } catch (error: any) {
        if (error) {
          error.infoStatus = "error";
        }
        throw error;
      }
    },
    patch: async (body?: any, key?: string, requestParams?: boolean, queryParams?: boolean) => {
      try {
        return await instance.patch(
          urlBuild(path, key, requestParams, queryParams),
          body,
          getRequestHeader(body),
        );
      } catch (error: any) {
        error.infoStatus = "error";
        throw error;
      }
    },

    delete: async (key?: string, requestParams?: boolean, queryParams?: boolean, body?: any) => {
      try {
        return await instance.delete(urlBuild(path, key, requestParams, queryParams), {
          ...header,
          data: body,
        });
      } catch (error: any) {
        error.infoStatus = "error";
        throw error;
      }
    },
    deleteWithBody: async (body?: any) => {
      try {
        return await instance.delete(urlBuild(path), { ...header, data: body });
      } catch (error: any) {
        error.infoStatus = "error";
        throw error;
      }
    },
  };

  return request;
}
